import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Auth from './components/Auth';
import Header from './components/Header';
import MobileApp from './components/mobile/MobileApp';
import DesktopApp from './components/desktop/DesktopApp';
import Sidebar from './components/Sidebar';
import AskView from './components/AskView';
import PeopleGrid from './components/PeopleGrid';
import ListView from './components/ListView';
import StrategyView from './components/StrategyView';
import SettingsView from './components/SettingsView';
import FloatingParticles from './components/FloatingParticles';
import CursorTrail from './components/CursorTrail';
import NotificationBell from './components/NotificationBell';
import UniversalChat from './components/UniversalChat';
import DonorPanel from './components/DonorPanel';
import LandingPage from './components/LandingPage';
import PlanSelection from './components/PlanSelection';
import PaywallGate from './components/PaywallGate';
import TrialBanner from './components/TrialBanner';
import AddPersonModal from './components/AddPersonModal';
import CreateGroupModal from './components/CreateGroupModal';
import { supabase } from './lib/supabase';
import { updateDonorByName, deleteDonorByName, fetchLists, createList, updateList, deleteList, supabaseListToAppFormat, fetchGroups, createGroup, updateGroup, deleteGroup, supabaseGroupToAppFormat, fetchSubscription, fetchDonors as fetchSupabaseDonors, supabaseDonorToAppFormat, fetchChatHistory, saveChatMessage } from './lib/supabaseDb';
import { authFetch, authPost } from './lib/authFetch';
import { cache } from './lib/cache';
import { isLocalModeEnabled, getLocalDonors, getLocalGroups, getLocalLists } from './lib/localMode';
import './App.css';

function App({ user, signOut, subscription, onUpgrade }) {
  const [view, setView] = useState('ask');
  const [showSettings, setShowSettings] = useState(false);
  // Data mode is always 'real' now - imaginary mode has been removed
  const dataMode = 'real';

  // Migrate any old localStorage values on mount
  React.useEffect(() => {
    if (localStorage.getItem('penelope-data-mode') !== 'real') {
      localStorage.setItem('penelope-data-mode', 'real');
    }
  }, []);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('penelope-theme') || 'dark';
  });

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // New desktop UI is now the default - use ?desktop=old to revert
  const [useNewDesktop] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const desktopParam = urlParams.get('desktop');
    // Only show old desktop if explicitly requested via URL
    if (desktopParam === 'old') {
      return false;
    }
    // Default to new desktop
    return true;
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [showStrategy, setShowStrategy] = useState(false);
  const [twoColumnLayout, setTwoColumnLayout] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedDonor, setSelectedDonor] = useState(null);

  // People grid refresh trigger
  const [peopleRefreshTrigger, setPeopleRefreshTrigger] = useState(0);

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpEmail, setHelpEmail] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [helpSuccess, setHelpSuccess] = useState(false);
  const [helpError, setHelpError] = useState('');

  // Saved lists state
  const [savedLists, setSavedLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [lastDonorUpdate, setLastDonorUpdate] = useState(0);
  const [listRefreshTrigger, setListRefreshTrigger] = useState(0);

  // Background refresh function - refreshes all stale lists when donors change
  // forceRegenerate: if true, regenerate list groupings based on current donor data
  const refreshAllStaleLists = async (currentTimestamp, forceRegenerate = false) => {
    if (!savedLists.length) return;

    // When forceRegenerate is true, refresh ALL lists/tables (not just stale ones)
    const itemsToRefresh = forceRegenerate
      ? savedLists
      : savedLists.filter(item => {
          const itemLastEvaluated = item.lastEvaluated ? new Date(item.lastEvaluated).getTime() : 0;
          return currentTimestamp > itemLastEvaluated;
        });

    if (itemsToRefresh.length === 0) {
      return;
    }

    // Refresh all lists and tables in parallel (background, non-blocking)
    itemsToRefresh.forEach(async (item) => {
      try {
        // Handle tables differently from lists
        if (item.type === 'table') {
          const response = await authPost('/api/tables/refresh', {
            tableId: item.id,
            mode: dataMode,
            columnPrompt: item.columnPrompt,
            groupId: item.groupId,
            tableName: item.name
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.table) {
              // Update the table in state, preserving existing fields
              setSavedLists(prev => prev.map(l =>
                l.id === item.id ? { ...l, ...data.table } : l
              ));
            }
          }
        } else {
          // Regular list refresh
          const response = await authPost('/api/lists/refresh', {
            listId: item.id,
            mode: dataMode,
            organizationPrompt: item.organizationPrompt,
            listName: item.name,
            currentGroups: item.groups,
            groupId: item.groupId,
            forceRegenerate
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.list) {
              // Update the list in state
              setSavedLists(prev => prev.map(l =>
                l.id === data.list.id ? { ...l, ...data.list } : l
              ));
            }
          }
        }
      } catch (error) {
        console.error(`Failed to background refresh ${item.name}:`, error);
      }
    });
  };

  // Groups state (for categorizing people)
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Notebooks state
  const [notebooks, setNotebooks] = useState([]);
  const [activeNotebookId, setActiveNotebookId] = useState(null);

  // Universal chat state - tied to user account
  const [conversationHistory, setConversationHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
    const [pendingListData, setPendingListData] = useState(null);

  // Donor panel state
  const [donorPanelOpen, setDonorPanelOpen] = useState(false);
  const [donorPanelName, setDonorPanelName] = useState(null);
  const [donorPanelInitialData, setDonorPanelInitialData] = useState(null);
  const [donorPanelExpanded, setDonorPanelExpanded] = useState(false);

  // All donors for sidebar search
  const [allDonors, setAllDonors] = useState([]);

  // Group selection modal state (for adding new donors)
  const [showGroupSelectModal, setShowGroupSelectModal] = useState(false);
  const [pendingDonorAction, setPendingDonorAction] = useState(null);
  const [newGroupNameInput, setNewGroupNameInput] = useState('');
  const [groupExampleIndex, setGroupExampleIndex] = useState(0);
  const [groupExampleAnimating, setGroupExampleAnimating] = useState(false);

  // Group name examples for animated placeholder
  const GROUP_EXAMPLES = ['Investors', 'Donors', 'Customers', 'Prospects', 'Board Members'];

  // Add Person modal state (from sidebar + button)
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [isFirstPersonFlow, setIsFirstPersonFlow] = useState(false);

  // Create Group modal state (from sidebar + button)
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);

  // Sidebar hint for visiting first group (onboarding)
  const [, setShowGroupsHint] = useState(false);

  // Track mobile sheet open state (to hide bottom icons)
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Onboarding welcome modal state
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('penelope-tour-completed');
  });

  const dismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('penelope-tour-completed', 'true');
  };

  // Function to open donor panel from anywhere
  // Pass initialData for newly created donors to avoid refetch race condition
  const openDonorPanel = (donorName, initialData = null) => {
    setDonorPanelName(donorName);
    setDonorPanelInitialData(initialData);
    setDonorPanelOpen(true);
  };

  const closeDonorPanel = () => {
    setDonorPanelOpen(false);
    setDonorPanelName(null);
    setDonorPanelInitialData(null);
    setDonorPanelExpanded(false);
  };

  useEffect(() => {
    localStorage.setItem('penelope-theme', theme);
  }, [theme]);

  // FAST DATA LOADING: Load cached data instantly, then refresh in parallel
  useEffect(() => {
    if (!user?.id) return;

    // Clean up old localStorage keys (migration to Supabase)
    localStorage.removeItem('penelope-chat-history');
    localStorage.removeItem(`penelope-chat-history-${user.id}`);

    // Step 1: Show cached data INSTANTLY
    const cachedHistory = cache.get('chatHistory', user.id);
    const cachedLists = cache.get('lists', user.id);
    const cachedGroups = cache.get('groups', user.id);
    const cachedDonors = cache.get('donors', user.id);

    if (cachedHistory?.data) {
      setConversationHistory(cachedHistory.data);
      setHistoryLoaded(true);
    }
    if (cachedLists?.data) {
      setSavedLists(cachedLists.data);
    }
    if (cachedGroups?.data) {
      setGroups(cachedGroups.data);
      setGroupsLoaded(true);
    }
    if (cachedDonors?.data) {
      setAllDonors(cachedDonors.data);
    }

    // Step 2: Fetch fresh data in PARALLEL (background refresh)
    const loadAllData = async () => {
      const startTime = Date.now();

      try {
        // Fetch everything in parallel
        const [historyResult, listsResult, groupsResult, donorsResult, serverResult] = await Promise.allSettled([
          fetchChatHistory(user.id),
          // Local Mode: read from the browser instead of Supabase. Lists are
          // already stored in app-ready shape locally (unlike donors/groups),
          // so this skips the raw-row formatter below for this path.
          isLocalModeEnabled() ? Promise.resolve(getLocalLists()) : fetchLists(user.id),
          isLocalModeEnabled() ? Promise.resolve(getLocalGroups()) : fetchGroups(user.id),
          isLocalModeEnabled() ? Promise.resolve(getLocalDonors()) : fetchSupabaseDonors(user.id),
          authFetch(`/api/lists?mode=${dataMode}`)
        ]);

        // Process chat history
        if (historyResult.status === 'fulfilled') {
          setConversationHistory(historyResult.value);
          cache.set('chatHistory', historyResult.value, user.id);
        }
        setHistoryLoaded(true);

        // Process lists
        if (listsResult.status === 'fulfilled') {
          const formattedLists = isLocalModeEnabled()
            ? listsResult.value
            : listsResult.value.map(supabaseListToAppFormat);
          setSavedLists(formattedLists);
          cache.set('lists', formattedLists, user.id);
        }

        // Process groups
        if (groupsResult.status === 'fulfilled') {
          const formattedGroups = groupsResult.value.map(supabaseGroupToAppFormat);
          setGroups(formattedGroups);
          cache.set('groups', formattedGroups, user.id);
        }
        setGroupsLoaded(true);

        // Process donors for sidebar search
        if (donorsResult.status === 'fulfilled') {
          const formattedDonors = donorsResult.value.map(supabaseDonorToAppFormat);
          setAllDonors(formattedDonors);
          cache.set('donors', formattedDonors, user.id);
        }

        // Process lastDonorUpdate from server
        if (serverResult.status === 'fulfilled' && serverResult.value.ok) {
          const serverData = await serverResult.value.json();
          if (serverData.lastDonorUpdate) {
            setLastDonorUpdate(serverData.lastDonorUpdate);
          }
        }

        console.log(`All data loaded in ${Date.now() - startTime}ms (parallel)`);
      } catch (error) {
        console.error('Error loading data:', error);
        setHistoryLoaded(true);
        setGroupsLoaded(true);
      }
    };

    loadAllData();
  }, [user?.id, dataMode]);

  // Fetch notebooks on mount and when dataMode changes
  useEffect(() => {
    const fetchNotebooks = async () => {
      try {
        const response = await authFetch(`/api/notebooks?mode=${dataMode}`);
        if (response.ok) {
          const data = await response.json();
          setNotebooks(data.notebooks || []);
        }
      } catch (error) {
        console.log('Could not fetch notebooks');
      }
    };
    fetchNotebooks();
  }, [dataMode]);

  // Refresh donors list when people are added/updated
  useEffect(() => {
    if (!user?.id || peopleRefreshTrigger === 0) return;
    const refreshDonors = async () => {
      try {
        const donors = isLocalModeEnabled() ? getLocalDonors() : await fetchSupabaseDonors(user.id);
        const formattedDonors = donors.map(supabaseDonorToAppFormat);
        setAllDonors(formattedDonors);
        cache.set('donors', formattedDonors, user.id);
      } catch (error) {
        console.error('Error refreshing donors:', error);
      }
    };
    refreshDonors();
  }, [peopleRefreshTrigger, user?.id]);

  // Cycle through group name examples when modal is open
  useEffect(() => {
    if (!showGroupSelectModal || newGroupNameInput) return;

    const interval = setInterval(() => {
      setGroupExampleAnimating(true);
      setTimeout(() => {
        setGroupExampleIndex(prev => (prev + 1) % GROUP_EXAMPLES.length);
        setGroupExampleAnimating(false);
      }, 150);
    }, 2000);

    return () => clearInterval(interval);
  }, [showGroupSelectModal, newGroupNameInput, GROUP_EXAMPLES.length]);

  const handleSaveList = async (newList, groupId = null) => {
    // If user is logged in, sync to Supabase
    if (user?.id) {
      try {
        // Check if this is an existing list (has a UUID, not a local list-{timestamp} or table-{timestamp} ID)
        const isExistingList = newList.id && !newList.id.startsWith('list-') && !newList.id.startsWith('table-');
        const isTable = newList.type === 'table';

        if (isExistingList) {
          // Update existing list/table
          const updates = {
            name: newList.name,
            groups: newList.groups,
            organization_prompt: newList.organizationPrompt
          };
          // Add table-specific fields if this is a table
          if (isTable) {
            updates.type = 'table';
            updates.columns = newList.columns;
            updates.rows = newList.rows;
            updates.column_prompt = newList.columnPrompt;
          }
          const updatedList = await updateList(newList.id, updates);
          if (updatedList) {
            const listWithGroupId = {
              ...newList,
              groupId: groupId || newList.groupId
            };
            setSavedLists(prev => prev.map(l => l.id === newList.id ? listWithGroupId : l));
            console.log('Updated list in Supabase:', newList.name);
            return listWithGroupId;
          }
        } else {
          // Create new list/table
          const tableOptions = isTable ? {
            type: 'table',
            columns: newList.columns,
            rows: newList.rows,
            columnPrompt: newList.columnPrompt
          } : {};
          const supabaseList = await createList(user.id, newList.name, newList.groups, newList.organizationPrompt, groupId, tableOptions);
          if (supabaseList) {
            // Use the Supabase-generated ID
            const listWithSupabaseId = {
              ...newList,
              id: supabaseList.id,
              groupId: groupId
            };
            setSavedLists(prev => [listWithSupabaseId, ...prev.filter(l => l.id !== newList.id && l.id !== supabaseList.id)]);
            setActiveListId(supabaseList.id);
            console.log('Created list in Supabase:', newList.name, 'id:', supabaseList.id, 'groupId:', groupId, 'type:', isTable ? 'table' : 'list');
            return listWithSupabaseId;
          }
        }
      } catch (err) {
        console.error('Failed to sync list to Supabase:', err);
      }
    }

    // Fallback: use local ID if not logged in or Supabase failed
    const listWithGroupId = { ...newList, groupId };
    setSavedLists(prev => [listWithGroupId, ...prev.filter(l => l.id !== newList.id)]);
    setActiveListId(newList.id);
    return listWithGroupId;
  };

  const handleSelectList = (listId) => {
    setActiveListId(listId);
    // Only switch to lists view for standalone lists (no groupId)
    // Lists within groups are shown inline in PeopleGrid
    if (listId) {
      const list = savedLists.find(l => l.id === listId);
      if (list && !list.groupId) {
        setView('lists');
      }
      // If list has groupId, stay on current view (people) - it renders inline
    }
  };

  const handleListRefreshed = async (refreshedList) => {
    // Update the list in savedLists with the refreshed data, preserving existing properties
    setSavedLists(prev => prev.map(l =>
      l.id === refreshedList.id ? { ...l, ...refreshedList } : l
    ));

    // Also save to Supabase if user is logged in
    if (user?.id && refreshedList.id) {
      try {
        const updates = {
          groups: refreshedList.groups,
          updated_at: new Date().toISOString(),
          last_evaluated: refreshedList.lastEvaluated || new Date().toISOString()
        };
        // Add table-specific fields if this is a table
        if (refreshedList.type === 'table') {
          updates.type = 'table';
          updates.columns = refreshedList.columns;
          updates.rows = refreshedList.rows;
        }
        await updateList(refreshedList.id, updates);
      } catch (err) {
        console.error('Failed to save refreshed list to Supabase:', err);
      }
    }
  };

  const handleDeleteList = async (listId) => {
    const listToDelete = savedLists.find(l => l.id === listId);
    console.log('handleDeleteList called:', { listId, listToDelete, dataMode, userId: user?.id });
    if (!listToDelete) return;

    // Remove from local state
    setSavedLists(prev => prev.filter(l => l.id !== listId));

    // If the deleted list was active, clear the selection
    if (activeListId === listId) {
      setActiveListId(null);
    }

    // Delete from Supabase if user is logged in (regardless of dataMode)
    if (user?.id) {
      try {
        const result = await deleteList(listId);
        console.log('Deleted list from Supabase:', listToDelete.name, 'result:', result);
      } catch (err) {
        console.error('Failed to delete list from Supabase:', err);
      }
    }
  };

  // Group handlers
  const handleCreateGroup = async (name, context = null) => {
    if (!user?.id) return null;
    try {
      const newGroup = await createGroup(user.id, name, '#8b5cf6', context);
      if (newGroup) {
        const formatted = supabaseGroupToAppFormat(newGroup);
        setGroups(prev => {
          const updated = [...prev, formatted];
          cache.set('groups', updated, user.id);
          return updated;
        });
        return formatted;
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    }
    return null;
  };

  const handleUpdateGroup = async (groupId, updates) => {
    if (!user?.id || !groupId) return null;
    try {
      const updatedGroup = await updateGroup(groupId, updates);
      if (updatedGroup) {
        const formatted = supabaseGroupToAppFormat(updatedGroup);
        setGroups(prev => {
          const updated = prev.map(g => g.id === groupId ? formatted : g);
          cache.set('groups', updated, user.id);
          return updated;
        });
        return formatted;
      }
    } catch (error) {
      console.error('Failed to update group:', error);
    }
    return null;
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      // First, delete all donors in this group
      const donorsToDelete = allDonors.filter(d => d.groupId === groupId);
      console.log(`Deleting ${donorsToDelete.length} donors in group ${groupId}`);

      // Delete each donor
      for (const donor of donorsToDelete) {
        try {
          await deleteDonorByName(user.id, donor.name);
          console.log('Deleted donor:', donor.name);
        } catch (err) {
          console.error('Failed to delete donor:', donor.name, err);
        }
      }

      // Update allDonors state to remove deleted donors
      if (donorsToDelete.length > 0) {
        setAllDonors(prev => {
          const updated = prev.filter(d => d.groupId !== groupId);
          cache.set('donors', updated, user?.id);
          return updated;
        });
      }

      // Now delete the group itself
      const success = await deleteGroup(groupId);
      if (success) {
        setGroups(prev => {
          const updated = prev.filter(g => g.id !== groupId);
          cache.set('groups', updated, user?.id);
          return updated;
        });
        if (activeGroupId === groupId) {
          setActiveGroupId(null);
        }

        // Trigger refresh to update any views showing donors
        setPeopleRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const handleSelectGroup = (groupId) => {
    setActiveGroupId(groupId);
    setView('people');
  };

  const handleSelectNotebook = (notebookId) => {
    setActiveNotebookId(notebookId);
    setShowStrategy(true);
  };

  const handleNewNotebook = () => {
    setActiveNotebookId(null); // Will create new notebook in StrategyView
    setShowStrategy(true);
  };

  const handleNotebooksUpdated = (updatedNotebooks) => {
    setNotebooks(updatedNotebooks);
  };

  const cycleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Universal chat handlers
  const handleAddMessage = async (message) => {
    const messageWithTimestamp = { ...message, timestamp: message.timestamp || new Date().toISOString() };
    setConversationHistory(prev => [...prev, messageWithTimestamp]);

    // Save to Supabase
    if (user?.id) {
      await saveChatMessage(user.id, messageWithTimestamp);
    }
  };

  // Help modal handlers
  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    if (!helpMessage.trim() || helpSubmitting) return;

    setHelpSubmitting(true);
    setHelpError('');

    try {
      if (supabase) {
        const { error } = await supabase
          .from('help_inquiries')
          .insert([
            {
              email: helpEmail.trim() || user?.email || null,
              message: helpMessage.trim(),
              source: 'app',
              created_at: new Date().toISOString()
            }
          ]);

        if (error) {
          console.error('Supabase error:', error);
          setHelpError('Something went wrong. Please try again.');
        } else {
          setHelpSuccess(true);
          setHelpEmail('');
          setHelpMessage('');
          // Close modal after delay
          setTimeout(() => {
            setShowHelpModal(false);
            setHelpSuccess(false);
          }, 2000);
        }
      } else {
        setHelpError('Help system is currently unavailable.');
      }
    } catch (error) {
      console.error('Help submit error:', error);
      setHelpError('Something went wrong. Please try again.');
    } finally {
      setHelpSubmitting(false);
    }
  };

  const closeHelpModal = () => {
    setShowHelpModal(false);
    setHelpError('');
    setHelpSuccess(false);
  };

  // Complete adding a donor after group selection
  const completeAddDonor = async (donorData, groupId) => {
    console.log('completeAddDonor:', { donorData, groupId });

    // Call server to add donor with groupId
    try {
      await authPost('/api/donors/add-with-group', {
        name: donorData.name,
        amount: donorData.amount,
        notes: donorData.notes,
        groupId: groupId,
        mode: dataMode
      });
    } catch (error) {
      console.error('Failed to add donor with group:', error);
    }

    // Immediately add to allDonors for sidebar search (optimistic update)
    const newDonor = {
      name: donorData.name,
      amount: donorData.amount || 'Unknown',
      groupId: groupId
    };
    setAllDonors(prev => {
      // Check if already exists (avoid duplicates)
      if (prev.some(d => d.name === donorData.name)) {
        return prev;
      }
      return [...prev, newDonor];
    });

    // Trigger background refresh for full data sync
    if (dataMode === 'real') {
      const timestamp = Date.now();
      setLastDonorUpdate(timestamp);
      setListRefreshTrigger(prev => prev + 1);
      await new Promise(resolve => setTimeout(resolve, 300));
      refreshAllStaleLists(timestamp);
    }
    setPeopleRefreshTrigger(prev => prev + 1);

    // Open the donor panel
    if (donorData.name) {
      const initialData = {
        name: donorData.name,
        amount: donorData.amount || 'Unknown',
        narrative: donorData.notes || '',
        lastContact: new Date().toISOString().split('T')[0],
        nextMeeting: donorData.nextMeeting || 'Not scheduled',
        nextTask: donorData.nextTask || 'No tasks',
        groupId: groupId
      };
      openDonorPanel(donorData.name, initialData);

      // Navigate to the group and clear any active list
      setActiveGroupId(groupId);
      setActiveListId(null);
      setView('people');
    }

    // Clear the modal state
    setShowGroupSelectModal(false);
    setPendingDonorAction(null);
    setNewGroupNameInput('');
  };

  // Handle selecting a group for the pending donor
  const handleGroupSelectForDonor = async (groupId) => {
    if (pendingDonorAction) {
      await completeAddDonor(pendingDonorAction.data, groupId);
    }
  };

  // Handle adding a person from the AddPersonModal (sidebar + button)
  const handleAddPersonFromModal = async ({ name, groupId }) => {
    const wasFirstPerson = isFirstPersonFlow;
    await completeAddDonor({ name }, groupId);

    // If this was the first person flow, navigate to their profile and show groups hint
    if (wasFirstPerson) {
      setIsFirstPersonFlow(false);
      // Navigate to their profile after a short delay
      setTimeout(() => {
        openDonorPanel(name);
        // Show the groups hint after they see the profile
        setTimeout(() => {
          setShowGroupsHint(true);
        }, 1500);
      }, 500);
    }
  };

  // Handle creating a new group and then adding the donor to it
  const handleCreateGroupForDonor = async () => {
    if (!newGroupNameInput.trim() || !pendingDonorAction) return;

    try {
      const newGroup = await handleCreateGroup(newGroupNameInput.trim());
      if (newGroup) {
        await completeAddDonor(pendingDonorAction.data, newGroup.id);
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleChatAction = async (action) => {
    if (!action) return;

    // Supabase sync for donor actions (whenever user is logged in)
    const shouldSyncToSupabase = !!user?.id;

    switch (action.type) {
      case 'navigate':
        setView(action.view || action.data?.view);
        break;
      case 'search':
        setView('people');
        break;
      case 'create_list':
        // Handle the new groups format from universal chat
        const listData = action.data || {};
        console.log('Creating list from chat:', listData);
        setActiveListId(null); // Clear any selected saved list
        setPendingListData({
          name: listData.name || 'New List',
          groups: listData.groups || [{ name: listData.name, people: listData.people || [] }]
        });
        setView('lists');
        break;
      case 'select_donor':
        const selectedDonorName = action.donorName || action.data?.name;
        if (selectedDonorName) {
          // Fetch donor data to get their group
          try {
            if (user?.id) {
              const supabaseDonors = isLocalModeEnabled() ? getLocalDonors() : await fetchSupabaseDonors(user.id);
              const donor = supabaseDonors.find(d => d.name === selectedDonorName);
              if (donor?.group_id) {
                setActiveGroupId(donor.group_id);
                setActiveListId(null);
              }
            }
          } catch (err) {
            console.error('Failed to fetch donor group:', err);
          }
          openDonorPanel(selectedDonorName);
          setView('people');
        }
        break;
      case 'add_donor':
        console.log('add_donor action received:', action);
        if (action.data?.name) {
          // Priority 1: If user is currently viewing a group, add to that group
          if (activeGroupId) {
            console.log('Adding to active group:', activeGroupId);
            await completeAddDonor(action.data, activeGroupId);
          } else {
            // Priority 2: Check if the user's message mentions a group name
            const userMessage = (action.originalMessage || '').toLowerCase();
            console.log('Checking message for group match:', userMessage);
            console.log('Available groups:', groups.map(g => g.name));

            // Check if any group name appears in the message
            const matchedGroup = groups.find(group => {
              const groupNameLower = group.name.toLowerCase();
              const groupNameSingular = groupNameLower.replace(/s$/, '');
              // Check for the group name or its singular form in the message
              return userMessage.includes(groupNameLower) ||
                     userMessage.includes(groupNameSingular);
            });

            if (matchedGroup) {
              // Auto-add to the matched group
              console.log('Auto-matched group:', matchedGroup.name);
              await completeAddDonor(action.data, matchedGroup.id);
            } else {
              // No match - show group selection modal
              console.log('No group match found, showing modal');
              setPendingDonorAction(action);
              setShowGroupSelectModal(true);
            }
          }
        }
        break;
      case 'add_donors':
        console.log('add_donors action received:', action);
        // Multiple donors added - refresh and open first donor's panel
        if (dataMode === 'real') {
          const timestamp = Date.now();
          setLastDonorUpdate(timestamp);
          setListRefreshTrigger(prev => prev + 1);
          await new Promise(resolve => setTimeout(resolve, 300));
          // Background refresh all stale lists
          refreshAllStaleLists(timestamp);
        }
        setPeopleRefreshTrigger(prev => prev + 1);
        // Open the first donor's panel
        if (action.data?.name) {
          const initialData = {
            name: action.data.name,
            amount: 'Unknown',
            narrative: 'New donor.',
            lastContact: new Date().toISOString().split('T')[0],
            nextMeeting: 'Not scheduled',
            nextTask: 'No tasks'
          };
          openDonorPanel(action.data.name, initialData);
          setView('people');
        }
        break;
      case 'update_donor':
        console.log('handleChatAction update_donor:', action);
        console.log('shouldSyncToSupabase:', shouldSyncToSupabase, 'action.data:', action.data);
        // Sync updates to Supabase (skip if server already synced)
        if (shouldSyncToSupabase && action.data?.name && !action.data?.serverSynced) {
          try {
            const updates = {};
            const actionUpdates = action.data.updates || {}; // Server sends updates in this nested object
            console.log('actionUpdates:', actionUpdates);

            // Check both top-level and nested updates object
            if (action.data.amount || actionUpdates.amount) {
              updates.amount = action.data.amount || actionUpdates.amount;
            }
            // For notes, append to existing narrative (same as server does)
            if (action.data.notes || actionUpdates.notes) {
              const newNote = action.data.notes || actionUpdates.notes;
              // We'll need to fetch current narrative and append
              // For now, just set the note as narrative update
              updates.narrative = newNote;
            }
            if (action.data.narrative || actionUpdates.narrative) {
              updates.narrative = action.data.narrative || actionUpdates.narrative;
            }
            // Sync extracted meetings/tasks
            if (action.data.nextMeeting) {
              updates.next_meeting = action.data.nextMeeting;
            }
            if (action.data.nextTask) {
              updates.next_task = action.data.nextTask;
            }

            console.log('Final updates to sync:', updates);
            if (Object.keys(updates).length > 0) {
              // Use appendNarrative=true since server sends just the new note, not the full narrative
              const result = await updateDonorByName(user.id, action.data.name, updates, true);
              console.log('Synced donor update to Supabase:', action.data.name, updates, 'result:', result);
            } else {
              console.log('No updates to sync - updates object is empty');
            }
          } catch (err) {
            console.error('Failed to sync update to Supabase:', err);
          }
        }
        // Always update lastDonorUpdate and trigger list refresh - server already synced to Supabase
        if (dataMode === 'real') {
          const timestamp = Date.now();
          setLastDonorUpdate(timestamp);
          setListRefreshTrigger(prev => prev + 1);
          // Background refresh all lists with force regenerate to re-evaluate based on updated info
          refreshAllStaleLists(timestamp, true);
        }
        break;
      case 'delete_donor':
        // Sync deletion to Supabase
        if (shouldSyncToSupabase && action.data?.name) {
          try {
            await deleteDonorByName(user.id, action.data.name);
            console.log('Synced donor deletion to Supabase:', action.data.name);
            // Update lastDonorUpdate so lists know to refresh
            const timestamp = Date.now();
            setLastDonorUpdate(timestamp);
            // Background refresh all stale lists
            refreshAllStaleLists(timestamp);
          } catch (err) {
            console.error('Failed to sync deletion to Supabase:', err);
          }
        }
        // Clear selection and go to people view
        setSelectedDonor(null);
        setView('people');
        break;
      default:
        break;
    }
  };

  const showTrialBanner = subscription?.plan === 'trial';

  // Convert allDonors array to object for MobileApp
  const donorsObject = allDonors.reduce((acc, donor) => {
    acc[donor.name] = donor;
    return acc;
  }, {});

  // Refresh donors function for MobileApp (and passed to DesktopApp as onRefreshDonors)
  const refreshDonors = async () => {
    try {
      const donors = isLocalModeEnabled() ? getLocalDonors() : await fetchSupabaseDonors(user.id);
      const formattedDonors = donors.map(supabaseDonorToAppFormat);
      setAllDonors(formattedDonors);
    } catch (error) {
      console.error('Error refreshing donors:', error);
    }
  };

  // MOBILE: Show completely different UI on mobile
  if (isMobile) {
    return (
      <MobileApp
        donors={donorsObject}
        groups={groups}
        savedLists={savedLists}
        onRefreshDonors={refreshDonors}
        user={user}
        subscription={subscription}
        onSignOut={signOut}
      />
    );
  }

  // NEW DESKTOP: Show new desktop UI when enabled via ?desktop=new
  if (useNewDesktop) {
    return (
      <DesktopApp
        donors={donorsObject}
        groups={groups}
        savedLists={savedLists}
        savedTables={savedLists.filter(l => l.type === 'table')}
        onRefreshDonors={refreshDonors}
        user={user}
        subscription={subscription}
        onSignOut={signOut}
      />
    );
  }

  return (
    <div className={`app ${theme}${showTrialBanner ? ' has-trial-banner' : ''}`}>
      {/* Welcome Modal */}
      {showWelcome && (
        <div className="welcome-modal-overlay" onClick={dismissWelcome}>
          <div className="welcome-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Welcome to knowroom</h2>
            <div className="welcome-steps">
              <div className="welcome-step">
                <span className="welcome-icon">💬</span>
                <div>
                  <strong>Ask anything</strong>
                  <p>Use the search bar to add donors, ask questions, or get insights about your relationships.</p>
                </div>
              </div>
              <div className="welcome-step">
                <span className="welcome-icon">👥</span>
                <div>
                  <strong>Manage your people</strong>
                  <p>Click the arrow on the left to open the menu and navigate between People and Lists.</p>
                </div>
              </div>
              <div className="welcome-step">
                <span className="welcome-icon">📝</span>
                <div>
                  <strong>Take notes naturally</strong>
                  <p>Click any donor to see their story and add notes in plain language.</p>
                </div>
              </div>
            </div>
            <button className="welcome-button" onClick={dismissWelcome}>
              Get Started
            </button>
          </div>
        </div>
      )}
      <FloatingParticles />
      <Sidebar
        view={view}
        setView={(newView) => {
          setDonorPanelExpanded(false);
          setActiveGroupId(null);
          setView(newView);
        }}
        savedLists={savedLists}
        activeListId={activeListId}
        onSelectList={(listId) => {
          setDonorPanelExpanded(false);
          handleSelectList(listId);
        }}
        onDeleteList={handleDeleteList}
        notebooks={notebooks}
        activeNotebookId={activeNotebookId}
        onSelectNotebook={(notebookId) => {
          setDonorPanelExpanded(false);
          handleSelectNotebook(notebookId);
        }}
        onNewNotebook={() => {
          setDonorPanelExpanded(false);
          handleNewNotebook();
        }}
        showStrategy={showStrategy}
        groups={groups}
        activeGroupId={activeGroupId}
        onSelectGroup={(groupId) => {
          setDonorPanelExpanded(false);
          handleSelectGroup(groupId);
        }}
        onCreateGroup={() => setShowCreateGroupModal(true)}
        onDeleteGroup={handleDeleteGroup}
        showSettings={showSettings}
        onShowSettings={setShowSettings}
        onAddPerson={(name) => {
          if (name) {
            // If a name is passed from search, pre-fill it
            setShowAddPersonModal(true);
            // Store the name to pass to modal
            setPendingDonorAction({ type: 'add_from_search', name });
          } else {
            setShowAddPersonModal(true);
          }
        }}
        donors={allDonors}
        onSelectPerson={(donor) => {
          openDonorPanel(donor.name);
        }}
      />
      <Header onLogoClick={() => setShowSettings(true)} />
      {/* When donor panel is expanded, show it as main content */}
      {donorPanelExpanded ? (
        <DonorPanel
          isOpen={donorPanelOpen}
          onClose={closeDonorPanel}
          donorName={donorPanelName}
          dataMode={dataMode}
          conversationHistory={conversationHistory}
          onAddMessage={handleAddMessage}
          isExpanded={donorPanelExpanded}
          onExpandedChange={setDonorPanelExpanded}
          isInline={true}
          user={user}
          onAction={handleChatAction}
          initialData={donorPanelInitialData}
          groups={groups}
        />
      ) : (
        <>
          {view === 'ask' && (
            <AskView
              dataMode={dataMode}
              onAction={handleChatAction}
              conversationHistory={conversationHistory}
              onAddMessage={handleAddMessage}
              groups={groups}
              onNavigate={setView}
              dataLoaded={historyLoaded && groupsLoaded}
            />
          )}
          {(view === 'people' || view === 'allPeople') && (
            <PeopleGrid
              dataMode={dataMode}
              onSelectDonor={openDonorPanel}
              conversationHistory={conversationHistory}
              onAddMessage={handleAddMessage}
              onAction={handleChatAction}
              user={user}
              refreshTrigger={peopleRefreshTrigger}
              groups={groups}
              activeGroupId={activeGroupId}
              onCreateGroup={handleCreateGroup}
              onUpdateGroup={handleUpdateGroup}
              onSelectGroup={handleSelectGroup}
              savedLists={savedLists}
              activeListId={activeListId}
              onSaveList={handleSaveList}
              onSelectList={handleSelectList}
              onDeleteList={handleDeleteList}
              showAllPeople={view === 'allPeople'}
              onAddPerson={() => setShowAddPersonModal(true)}
              onMobileSheetChange={setMobileSheetOpen}
            />
          )}
          {view === 'lists' && (
            <ListView
              dataMode={dataMode}
              savedLists={savedLists}
              onSaveList={handleSaveList}
              onSelectList={handleSelectList}
              activeListId={activeListId}
              onSelectDonor={openDonorPanel}
              pendingListData={pendingListData}
              onListDataConsumed={() => setPendingListData(null)}
              onAction={handleChatAction}
              conversationHistory={conversationHistory}
              onAddMessage={handleAddMessage}
              lastDonorUpdate={lastDonorUpdate}
              onListRefreshed={handleListRefreshed}
              listRefreshTrigger={listRefreshTrigger}
            />
          )}
        </>
      )}
      {/* Strategy Modal */}
      {showStrategy && (
        <div className="strategy-modal-overlay" onClick={() => setShowStrategy(false)}>
          <div className="strategy-modal" onClick={(e) => e.stopPropagation()}>
            <button className="strategy-modal-close" onClick={() => setShowStrategy(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <StrategyView
              dataMode={dataMode}
              notebooks={notebooks}
              activeNotebookId={activeNotebookId}
              onNotebooksUpdated={handleNotebooksUpdated}
            />
          </div>
        </div>
      )}
      {showSettings && (
        <SettingsView
          dataMode={dataMode}
          onClose={() => setShowSettings(false)}
          user={user}
          subscription={subscription}
          onSignOut={signOut}
        />
      )}

      {/* Notification bell and quick add - hidden when mobile sheet is open */}
      {!mobileSheetOpen && (
        <>
          <NotificationBell
            dataMode={dataMode}
            onSelectDonor={(donorName) => {
              setSelectedDonor(donorName);
              setView('people');
            }}
          />
          <button
            className="quick-add-person"
            onClick={() => setShowAddPersonModal(true)}
            title="Add person"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <line x1="9" y1="14" x2="15" y2="14" />
            </svg>
          </button>
        </>
      )}

      {/* Layout toggle */}
      <button
        className="layout-toggle"
        onClick={() => setTwoColumnLayout(!twoColumnLayout)}
        aria-label={twoColumnLayout ? "Single column" : "Two columns"}
      >
        {twoColumnLayout ? (
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2" width="14" height="14" rx="2" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2" width="6" height="14" rx="2" />
            <rect x="10" y="2" width="6" height="14" rx="2" />
          </svg>
        )}
      </button>

      {/* Bottom icon buttons - hidden when mobile sheet is open */}
      {!mobileSheetOpen && (
        <div className="bottom-icons">
          {/* Styles/Theme toggle */}
          <button
            className="bottom-icon-button"
            onClick={cycleTheme}
            aria-label="Toggle theme"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a10 10 0 0 1 0 20" fill="currentColor" />
            </svg>
          </button>
          {/* Help button */}
          <button
            className="bottom-icon-button"
            onClick={() => setShowHelpModal(true)}
            aria-label="Help"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </div>
      )}

      {/* Gentle stars for magic theme */}
      {theme === 'magic' && (
        <>
          <div className="stars-container">
            {[...Array(40)].map((_, i) => (
              <div key={i} className="star" style={{
                '--delay': `${Math.random() * 8}s`,
                '--duration': `${4 + Math.random() * 6}s`,
                '--top': `${Math.random() * 100}%`,
                '--left': `${Math.random() * 100}%`,
                '--size': `${1 + Math.random() * 2}px`,
              }} />
            ))}
          </div>
          <CursorTrail />
        </>
      )}

      {/* Orbs for midnight theme */}
      {theme === 'midnight' && (
        <>
          <div className="midnight-noise"></div>
          <div className="midnight-orb midnight-orb-1"></div>
          <div className="midnight-orb midnight-orb-2"></div>
          <div className="midnight-orb midnight-orb-3"></div>
        </>
      )}

      {/* Universal Chat - hidden on Ask, Lists, People, and allPeople views since they have their own */}
      {view !== 'ask' && view !== 'lists' && view !== 'people' && view !== 'allPeople' && (
        <UniversalChat
          dataMode={dataMode}
          onNavigate={setView}
          onAction={handleChatAction}
          conversationHistory={conversationHistory}
          onAddMessage={handleAddMessage}
        />
      )}

      {/* Donor Panel - universal slide-over (only when not expanded inline) */}
      {!donorPanelExpanded && (
        <DonorPanel
          isOpen={donorPanelOpen}
          onClose={closeDonorPanel}
          donorName={donorPanelName}
          dataMode={dataMode}
          conversationHistory={conversationHistory}
          onAddMessage={handleAddMessage}
          isExpanded={donorPanelExpanded}
          onExpandedChange={setDonorPanelExpanded}
          user={user}
          onAction={handleChatAction}
          initialData={donorPanelInitialData}
          groups={groups}
        />
      )}

      {/* Group Selection Modal - shown when adding a new donor */}
      {showGroupSelectModal && pendingDonorAction && (
        <div className="group-select-overlay" onClick={() => {
          setShowGroupSelectModal(false);
          setPendingDonorAction(null);
          setNewGroupNameInput('');
        }}>
          <div className={`group-select-modal ${groups.length === 0 ? 'first-group' : ''}`} onClick={(e) => e.stopPropagation()}>
            {groups.length === 0 ? (
              <h2 className="rainbow-text">Create a Group to put {pendingDonorAction.data?.name} in</h2>
            ) : (
              <h2>Which group should I put {pendingDonorAction.data?.name} in?</h2>
            )}

            {groups.length > 0 && (
              <div className="group-select-list">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    className="group-select-item"
                    onClick={() => handleGroupSelectForDonor(group.id)}
                  >
                    <span className="group-select-color" style={{ backgroundColor: group.color }}></span>
                    {group.name}
                  </button>
                ))}
              </div>
            )}

            {groups.length > 0 && (
              <div className="group-select-divider">
                <span>or create a new group</span>
              </div>
            )}

            <div className="group-select-create">
              <div className="group-input-wrapper">
                <input
                  type="text"
                  value={newGroupNameInput}
                  onChange={(e) => setNewGroupNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newGroupNameInput.trim()) {
                      handleCreateGroupForDonor();
                    }
                  }}
                  autoFocus={groups.length === 0}
                />
                {!newGroupNameInput && (
                  <span className={`group-animated-placeholder rainbow-text ${groupExampleAnimating ? 'animating-out' : ''}`}>
                    {GROUP_EXAMPLES[groupExampleIndex]}
                  </span>
                )}
              </div>
              <button
                onClick={handleCreateGroupForDonor}
                disabled={!newGroupNameInput.trim()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Person Modal - from sidebar + button */}
      <AddPersonModal
        isOpen={showAddPersonModal}
        onClose={() => {
          setShowAddPersonModal(false);
          setPendingDonorAction(null);
        }}
        groups={groups}
        onAddPerson={handleAddPersonFromModal}
        onCreateGroup={handleCreateGroup}
        initialName={pendingDonorAction?.type === 'add_from_search' ? pendingDonorAction.name : ''}
        preselectedGroupId={activeGroupId}
      />

      {/* Create Group Modal - from sidebar + button */}
      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onCreateGroup={handleCreateGroup}
        onGroupCreated={handleSelectGroup}
      />

      {/* Help Modal */}
      {showHelpModal && (
        <div className="help-overlay" onClick={closeHelpModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="help-close" onClick={closeHelpModal}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="help-title">Need a little help?</h2>
            <p className="help-subtitle">Send us a message and we'll get back to you soon.</p>

            {helpSuccess ? (
              <div className="help-success">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Message sent! We'll be in touch.</span>
              </div>
            ) : (
              <form className="help-form" onSubmit={handleHelpSubmit}>
                <input
                  type="email"
                  className="help-input"
                  placeholder="Your email (optional)"
                  value={helpEmail}
                  onChange={(e) => setHelpEmail(e.target.value)}
                />
                <textarea
                  className="help-textarea"
                  placeholder="How can we help?"
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  rows={4}
                  required
                />
                {helpError && <p className="help-error">{helpError}</p>}
                <button type="submit" className="help-submit" disabled={helpSubmitting}>
                  {helpSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Wrapper component that handles auth
function AppWithAuth() {
  const { user, loading, signOut } = useAuth();
  const [showAuth, setShowAuth] = useState(() => {
    // Check if URL has /auth, /signup, or /login to restore state on refresh
    return window.location.pathname === '/auth' || window.location.pathname === '/signup' || window.location.pathname === '/login';
  }); // Show auth screen when user clicks login/signup
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showPlanSelection, setShowPlanSelection] = useState(false);

  // Check if using new desktop layout (hide trial banner there)
  const useNewDesktop = (() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('desktop') === 'new') return true;
    if (params.get('desktop') === 'old') return false;
    return localStorage.getItem('penelope-use-new-desktop') === 'true';
  })();

  // Check if returning from Stripe checkout
  const [isCheckoutReturn, setIsCheckoutReturn] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('checkout') === 'success';
  });

  // Timeout for checkout return - don't wait forever for session
  useEffect(() => {
    if (isCheckoutReturn) {
      const timeout = setTimeout(() => {
        setIsCheckoutReturn(false);
        window.history.replaceState({}, '', window.location.pathname);
      }, 5000); // 5 second timeout
      return () => clearTimeout(timeout);
    }
  }, [isCheckoutReturn]);

  // Handle Stripe checkout redirect
  useEffect(() => {
    if (isCheckoutReturn && user?.id) {
      // Clear the query param from URL
      window.history.replaceState({}, '', window.location.pathname);
      setIsCheckoutReturn(false);

      // Sync subscription with Stripe (handles local dev where webhooks can't reach)
      authPost('/api/sync-subscription', {})
        .then(res => res.json())
        .then(data => {
          console.log('Subscription sync result:', data);
          // Refetch subscription to get updated status
          return fetchSubscription(user.id);
        })
        .then(sub => {
          setSubscription(sub);
          setShowPlanSelection(false);
        })
        .catch(err => {
          console.error('Error syncing subscription:', err);
          // Still try to fetch subscription
          fetchSubscription(user.id).then(sub => {
            setSubscription(sub);
            setShowPlanSelection(false);
          });
        });
    }
  }, [user, isCheckoutReturn]);

  // Fetch subscription when user is authenticated
  useEffect(() => {
    if (user?.id) {
      fetchSubscription(user.id).then(sub => {
        setSubscription(sub);
        setCheckingSubscription(false);
      }).catch(() => {
        setCheckingSubscription(false);
      });
    } else {
      setCheckingSubscription(false);
    }
  }, [user]);

  const refetchSubscription = async () => {
    if (user?.id) {
      const sub = await fetchSubscription(user.id);
      setSubscription(sub);
      setShowPlanSelection(false);
    }
  };

  // Check if trial has expired
  const isTrialExpired = subscription?.plan === 'trial' &&
    new Date(subscription.trial_ends_at) < new Date();

  // Handle browser back button for auth screen
  useEffect(() => {
    const handlePopState = () => {
      // If we're on the root path and showAuth is true, hide auth
      if (window.location.pathname === '/' && showAuth) {
        setShowAuth(false);
      }
      // If we're on /auth, /signup, or /login, show auth
      if (window.location.pathname === '/auth' || window.location.pathname === '/signup' || window.location.pathname === '/login') {
        setShowAuth(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showAuth]);

  // Function to navigate to signup with browser history
  const navigateToSignup = () => {
    window.history.pushState({ auth: true }, '', '/signup');
    setShowAuth(true);
  };

  // Function to navigate to login with browser history
  const navigateToLogin = () => {
    window.history.pushState({ auth: true }, '', '/login');
    setShowAuth(true);
  };

  // Show loading while checking auth or subscription, or if returning from checkout
  if (loading || (user && checkingSubscription) || (isCheckoutReturn && !user)) {
    return (
      <div className="auth-loading">
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  // If Supabase is configured, use real auth
  if (supabase) {
    // Check if returning from successful checkout FIRST (before landing page check)
    if (!user) {
      const checkoutUser = localStorage.getItem('penelope-checkout-user');
      if (isCheckoutReturn || checkoutUser) {
        // Clear the checkout flags
        if (checkoutUser) localStorage.removeItem('penelope-checkout-user');
        if (window.location.search.includes('checkout=')) {
          window.history.replaceState({}, '', window.location.pathname);
        }
        return (
          <div className="checkout-success-page">
            <div className="checkout-success-card">
              <div className="checkout-success-icon">✓</div>
              <h1>Payment Successful!</h1>
              <p>Your Pro subscription is now active. Please sign in to continue.</p>
              <button onClick={() => {
                localStorage.setItem('penelope-entered-app', 'true');
                window.location.reload();
              }} className="checkout-success-button">
                Sign In
              </button>
            </div>
          </div>
        );
      }
    }

    // If not logged in, show landing page or auth screen
    if (!user) {
      if (showAuth) {
        const initialMode = window.location.pathname === '/signup' ? 'signup' : 'login';
        return <Auth initialMode={initialMode} />;
      }
      // Show landing page by default for non-authenticated users
      return (
        <LandingPage
          onEnterApp={navigateToSignup}
          onLogin={navigateToLogin}
        />
      );
    }

    // Show plan selection if trial expired or user clicked upgrade
    if (isTrialExpired || showPlanSelection) {
      return (
        <PlanSelection
          user={user}
          onComplete={refetchSubscription}
          onSignOut={signOut}
          onBack={!isTrialExpired ? () => setShowPlanSelection(false) : null}
        />
      );
    }

    // Show paywall gate if user has no subscription at all
    if (!subscription) {
      return (
        <PaywallGate
          user={user}
          onComplete={refetchSubscription}
          onSignOut={signOut}
        />
      );
    }

    // User is authenticated, show the app with trial banner if applicable (not on new desktop)
    return (
      <>
        {!useNewDesktop && (
          <TrialBanner
            subscription={subscription}
            onUpgrade={() => setShowPlanSelection(true)}
          />
        )}
        <App
          user={user}
          signOut={signOut}
          subscription={subscription}
          onUpgrade={() => setShowPlanSelection(true)}
        />
      </>
    );
  }

  // Fallback: Supabase not configured, show app without auth (demo mode)
  return <App />;
}

function AppRoot() {
  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

export default AppRoot;
